import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
type Line = { label: string; quantity?: number | null; percent: number | null };
export async function quoteDiscountRequest(body: object) {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error("Veuillez vous reconnecter.");
  const response = await fetch("/api/quotes/discounts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${data.session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || "Impossible de charger les remises.");
  return result;
}
export function QuoteDiscountEditor({
  caseId,
  quoteId,
  onClose,
}: {
  caseId: string;
  quoteId: string;
  onClose: () => void;
}) {
  const [quoteName, setQuoteName] = useState("");
  const [lines, setLines] = useState<Line[]>([]),
    [revision, setRevision] = useState(0),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [warning, setWarning] = useState(""),
    [confirmed, setConfirmed] = useState(false),
    [dirty, setDirty] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await quoteDiscountRequest({ action: "load", caseId, quoteId });
      setQuoteName(r.name || "Devis sélectionné");
      setLines(r.lines);
      setRevision(r.revision);
      setWarning(r.warning || "");
      setDirty(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de lecture.");
    } finally {
      setLoading(false);
    }
  }, [caseId, quoteId]);
  useEffect(() => {
    void load();
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [load]);
  const close = () => {
    if (!busy && (!dirty || window.confirm("Fermer sans enregistrer les remises ?"))) onClose();
  };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await quoteDiscountRequest({ action: "save", caseId, quoteId, revision, lines, confirmed });
      window.dispatchEvent(new Event("quote-discounts-updated"));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };
  const change = (index: number, patch: Partial<Line>) => {
    setLines((all) => all.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    setConfirmed(false);
    setDirty(true);
  };
  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-5"
      onClick={close}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-discount-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            close();
          }
          if (e.key === "Tab") {
            const a = Array.from(
              panel.current?.querySelectorAll<HTMLElement>(
                "button:not(:disabled),input:not(:disabled)",
              ) || [],
            );
            if (e.shiftKey && document.activeElement === a[0]) {
              e.preventDefault();
              a[a.length - 1]?.focus();
            } else if (!e.shiftKey && document.activeElement === a[a.length - 1]) {
              e.preventDefault();
              a[0]?.focus();
            }
          }
        }}
      >
        <header className="mb-4 flex items-center justify-between">
          <h2 id="quote-discount-title" className="text-xl font-semibold">
            Remises à mentionner dans l’email
          </h2>
          <button
            autoFocus
            type="button"
            onClick={close}
            disabled={busy}
            aria-label="Fermer les remises"
          >
            <X size={20} />
          </button>
        </header>
        <p className="mb-4 text-sm text-gray-600">
          {quoteName && <strong className="mb-2 block">{quoteName}</strong>}
            Renseignez uniquement les remises déjà comprises dans ce devis. Le PDF, ses prix et ses
          totaux restent inchangés.
        </p>
        {loading ? (
          <p role="status">Lecture des lignes du devis…</p>
        ) : (
          <form onSubmit={save}>
            <fieldset disabled={busy} className="space-y-4 disabled:opacity-60">
              {warning && (
                <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{warning}</p>
              )}
              <div className="space-y-3">
                {lines.map((line, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-3">
                    <label className="min-w-0 basis-full sm:basis-0 flex-1 text-xs text-gray-600">
                      Ligne {i + 1} du devis
                      <input
                        required
                        maxLength={240}
                        value={line.label}
                        onChange={(e) => change(i, { label: e.target.value })}
                        className="mt-1 block w-full rounded-lg border p-2.5 text-sm text-gray-900"
                      />
                    </label>
                    <label className="w-24 text-xs text-gray-600">
                      Quantité
                      <input type="number" min="0.001" max="1000000" step="0.001" value={line.quantity ?? ""} placeholder="À vérifier"
                        onChange={e => change(i, { quantity: e.target.value === "" ? null : Number(e.target.value) })}
                        className="mt-1 block w-full rounded-lg border p-2.5 text-sm text-gray-900" />
                    </label>
                    <label className="w-28 text-xs text-gray-600">
                      Remise (%)
                      <input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        value={line.percent ?? ""}
                        placeholder="Facultatif"
                        onChange={(e) =>
                          change(i, {
                            percent: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                        className="mt-1 block w-full rounded-lg border p-2.5 text-sm text-gray-900"
                      />
                    </label>
                    <button
                      type="button"
                      aria-label={`Retirer la ligne ${i + 1}`}
                      onClick={() => {
                        setLines((all) => all.filter((_, j) => i !== j));
                        setConfirmed(false);
                        setDirty(true);
                      }}
                      className="p-2.5 text-gray-400"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                disabled={lines.length >= 80}
                onClick={() => {
                  setLines((all) => [...all, { label: "", quantity: null, percent: null }]);
                  setDirty(true);
                }}
                className="inline-flex items-center gap-1 text-sm text-indigo-700"
              >
                <Plus size={16} />
                Ajouter une ligne manuellement
              </button>
              {lines.some((l) => l.percent !== null) && (
                <label className="flex gap-2 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-900">
                  <input
                    type="checkbox"
                    required
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  Je confirme que ces remises sont déjà comprises dans les prix du devis.
                </label>
              )}
              {error && (
                <p role="alert" className="text-sm text-red-700">
                  {error}
                </p>
              )}
              <div className="flex justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-lg border px-4 py-2.5 text-sm"
                >
                  Passer / fermer
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white"
                >
                  {busy ? "Enregistrement…" : "Enregistrer les mentions"}
                </button>
              </div>
            </fieldset>
          </form>
        )}
        {!loading && error && (
          <button type="button" onClick={() => void load()} className="mt-3 text-xs underline">
            Recharger les données
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
export function QuoteDiscountPreview({ caseId, quoteIds }: { caseId: string; quoteIds: string[] }) {
  const [html, setHtml] = useState(""),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const key = [...quoteIds].sort().join(",");
  useEffect(() => {
    let active = true;
    let version = 0;
    const load = async () => {
      const current = ++version;
      setLoading(true);
      try {
        const r = await quoteDiscountRequest({
          action: "preview",
          caseId,
          quoteIds: key ? key.split(",") : [],
        });
        if (active && current === version) {
          setHtml(r.html);
          setError("");
        }
      } catch (e) {
        if (active && current === version) {
          setHtml("");
          setError(e instanceof Error ? e.message : "Aperçu indisponible.");
        }
      } finally {
        if (active && current === version) setLoading(false);
      }
    };
    void load();
    window.addEventListener("quote-discounts-updated", load);
    window.addEventListener("focus", load);
    return () => {
      active = false;
      window.removeEventListener("quote-discounts-updated", load);
      window.removeEventListener("focus", load);
    };
  }, [caseId, key]);
  return (
    <div className="mt-3 rounded-lg border border-indigo-100 p-3">
      <p className="text-xs font-medium text-gray-600">
        Mentions de remise ajoutées automatiquement pour les devis joints
      </p>
      {loading ? (
        <p className="mt-2 text-xs text-gray-500">Chargement…</p>
      ) : error ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      ) : html ? (
        <iframe
          title="Aperçu des remises dans l’email"
          sandbox=""
          srcDoc={html}
          className="mt-2 h-64 w-full border-0"
        />
      ) : (
        <p className="mt-2 text-xs text-gray-500">Aucune remise renseignée sur ces devis.</p>
      )}
    </div>
  );
}
